import React from "react";
import { WebsiteLinkPreview, renderTextWithLinks } from "./WebsiteLinkPreview";

const YT_RE =
  /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?(?:[^&\s]*&)*v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})(?:[^\s]*)?/gi;

const VIMEO_RE =
  /(?:https?:\/\/)?(?:www\.)?(?:vimeo\.com\/(?:channels\/[^/\s]+\/|groups\/[^/\s]+\/videos\/|video\/)?|player\.vimeo\.com\/video\/)(\d+)(?:[^\s]*)?/gi;

/**
 * Split body into text segments and video embeds (Facebook-style auto-embed).
 * Supports YouTube and Vimeo.
 * @param {string} text
 * @returns {Array<{type:'text'|'youtube'|'vimeo', value:string}>}
 */
export function splitTextWithVideoEmbeds(text) {
  const raw = String(text || "");
  if (!raw) return [];

  const matches = [];
  const collect = (re, type) => {
    const r = new RegExp(re.source, "gi");
    let m;
    while ((m = r.exec(raw)) !== null) {
      if (m[1]) {
        matches.push({
          type,
          value: m[1],
          index: m.index,
          length: m[0].length,
        });
      }
    }
  };
  collect(YT_RE, "youtube");
  collect(VIMEO_RE, "vimeo");
  matches.sort((a, b) => a.index - b.index);

  const parts = [];
  let last = 0;
  matches.forEach((hit) => {
    if (hit.index < last) return;
    if (hit.index > last) {
      parts.push({ type: "text", value: raw.slice(last, hit.index) });
    }
    parts.push({ type: hit.type, value: hit.value });
    last = hit.index + hit.length;
  });
  if (last < raw.length) {
    parts.push({ type: "text", value: raw.slice(last) });
  }
  return parts.length ? parts : [{ type: "text", value: raw }];
}

/** @deprecated use splitTextWithVideoEmbeds */
export function splitTextWithYouTube(text) {
  return splitTextWithVideoEmbeds(text).filter(
    (p) => p.type === "text" || p.type === "youtube"
  );
}

export function extractYouTubeId(text) {
  if (!text) return null;
  const re = new RegExp(YT_RE.source, "i");
  const m = String(text).match(re);
  return m && m[1] ? m[1] : null;
}

export function YouTubeEmbed({ videoId, title = "YouTube video" }) {
  if (!videoId) return null;
  const src = "https://www.youtube.com/embed/" + encodeURIComponent(videoId);
  return (
    <div className="community-post__embed community-post__youtube">
      <iframe
        src={src}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
      />
    </div>
  );
}

export function VimeoEmbed({ videoId, title = "Vimeo video" }) {
  if (!videoId) return null;
  const src = "https://player.vimeo.com/video/" + encodeURIComponent(videoId);
  return (
    <div className="community-post__embed community-post__vimeo">
      <iframe
        src={src}
        title={title}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
      />
    </div>
  );
}

/**
 * Render comment body — at most one video embed (YouTube or Vimeo).
 * When structured embed/media is provided, prefer that over auto-scanning body.
 */
export function CommentBodyWithMedia({
  text,
  media = null,
  embed = null,
  linkPreview = null,
  className = "community-post__comment-text",
}) {
  const hasImage = !!(media && media.path);
  const structuredEmbed =
    embed && embed.id && (embed.provider === "youtube" || embed.provider === "vimeo")
      ? embed
      : null;
  // Website Link Preview card — YouTube/Vimeo keep priority (handled above/below).
  const card = linkPreview ? (
    <WebsiteLinkPreview preview={linkPreview} />
  ) : null;

  // Image comments: plain text only (no video embeds).
  if (hasImage) {
    return (
      <div className="community-post__comment-content">
        {text ? (
          <p className={className}>{renderTextWithLinks(text, "cimg")}</p>
        ) : null}
        <div className="community-post__comment-media">
          <img src={media.path} alt="" loading="lazy" />
        </div>
        {card}
      </div>
    );
  }

  if (structuredEmbed) {
    const parts = splitTextWithVideoEmbeds(text);
    const textOnly = parts
      .filter((p) => p.type === "text")
      .map((p) => p.value)
      .join("")
      .trim();
    return (
      <div className="community-post__comment-content">
        {textOnly ? (
          <p className={className}>{renderTextWithLinks(textOnly, "cse")}</p>
        ) : null}
        <div className="community-post__comment-embed">
          {structuredEmbed.provider === "youtube" ? (
            <YouTubeEmbed videoId={structuredEmbed.id} />
          ) : (
            <VimeoEmbed videoId={structuredEmbed.id} />
          )}
        </div>
        {card}
      </div>
    );
  }

  // Legacy: show at most the first embed from body.
  const parts = splitTextWithVideoEmbeds(text);
  let usedEmbed = false;
  const limited = [];
  parts.forEach((part) => {
    if (part.type === "youtube" || part.type === "vimeo") {
      if (!usedEmbed) {
        limited.push(part);
        usedEmbed = true;
      }
      return;
    }
    limited.push(part);
  });

  const hasEmbed = limited.some(
    (p) => p.type === "youtube" || p.type === "vimeo"
  );
  if (!hasEmbed) {
    if (!card) {
      return text ? (
        <p className={className}>{renderTextWithLinks(text, "cpl")}</p>
      ) : null;
    }
    return (
      <div className="community-post__comment-content">
        {text ? (
          <p className={className}>{renderTextWithLinks(text, "cpl")}</p>
        ) : null}
        {card}
      </div>
    );
  }

  return (
    <div className={className + " community-post__body--embeds"}>
      {limited.map((part, idx) => {
        if (part.type === "youtube") {
          return <YouTubeEmbed key={"yt-" + idx} videoId={part.value} />;
        }
        if (part.type === "vimeo") {
          return <VimeoEmbed key={"vim-" + idx} videoId={part.value} />;
        }
        const t = part.value;
        if (!t || !String(t).trim()) return null;
        return (
          <p key={"t-" + idx} className="community-post__body-seg">
            {renderTextWithLinks(t, "cseg-" + idx)}
          </p>
        );
      })}
      {card}
    </div>
  );
}

/**
 * Render post/comment body with automatic YouTube / Vimeo embeds.
 */
export function PostBodyWithEmbeds({
  text,
  className = "community-post__body",
  linkPreview = null,
}) {
  const parts = splitTextWithVideoEmbeds(text);
  const hasEmbed = parts.some((p) => p.type === "youtube" || p.type === "vimeo");
  // Website Link Preview card — appended after the body; YouTube/Vimeo embeds
  // above keep priority (backend only resolves non-video URLs).
  const card = linkPreview ? (
    <WebsiteLinkPreview preview={linkPreview} />
  ) : null;

  if (!hasEmbed) {
    if (!card) {
      return <p className={className}>{renderTextWithLinks(text, "pb")}</p>;
    }
    return (
      <div className={className + " community-post__body--linked"}>
        {text ? (
          <p className="community-post__body-seg">
            {renderTextWithLinks(text, "pb")}
          </p>
        ) : null}
        {card}
      </div>
    );
  }
  return (
    <div className={className + " community-post__body--embeds"}>
      {parts.map((part, idx) => {
        if (part.type === "youtube") {
          return <YouTubeEmbed key={"yt-" + idx} videoId={part.value} />;
        }
        if (part.type === "vimeo") {
          return <VimeoEmbed key={"vim-" + idx} videoId={part.value} />;
        }
        const t = part.value;
        if (!t || !String(t).trim()) return null;
        return (
          <p key={"t-" + idx} className="community-post__body-seg">
            {renderTextWithLinks(t, "pseg-" + idx)}
          </p>
        );
      })}
      {card}
    </div>
  );
}
